import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FreeStockTab } from '@/features/warehouse/freeStock/FreeStockTab'
import { FreeConversionModal } from '@/features/warehouse/freeStock/FreeConversionModal'
import { FreeConversionHistoryModal } from '@/features/warehouse/freeStock/FreeConversionHistoryModal'
import type { FreeStockItem } from '@/services/warehouse/freeStockApi'
import { IssueSlipsTab } from '@/features/warehouse/issueSlips/IssueSlipsTab'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageError } from '@/components/ui/PageAsyncState'
import { Badge } from '@/components/ui/Badge'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { formatDate, formatDateFull } from '@/lib/dateFormat'
import { PoStatusBadge, StockStatusBadge } from '@/features/proto/ProtoBadges'
import {
  formatReceivePct,
  poReceiveProgress,
} from '@/features/proto/poListMetrics'
import { useCan } from '@/features/auth/AuthContext'
import { DocumentHistoryTab } from '@/components/documents/DocumentHistoryTab'
import { VoidDocumentDialog } from '@/components/documents/VoidDocumentDialog'
import { formatDocumentError } from '@/utils/documentErrorMessages'
import { Edit2, Ban, X, AlertTriangle, Plus } from 'lucide-react'
import {
  getStockOverview,
  listStockMovements,
  recordSupplierReturn,
  recordCustomerReturn,
  recordReworkOrder,
  getStockIssue,
  patchStockIssue,
  voidStockIssue,
  getStockIssueHistory,
  getSupplierReturn,
  patchSupplierReturn,
  voidSupplierReturn,
  getSupplierReturnHistory,
  type MaterialStockOverviewItem,
  type StockMovementDto,
  type StockIssueDto,
  type SupplierReturnDto,
  listStockInbounds,
  getStockInbound,
  patchStockInbound,
  voidStockInbound,
  STOCK_INBOUND_SOURCE_LABELS,
  type StockInboundDto,
  type StockInboundSource,
  InventoryApiClientError,
} from '@/services/inventory'
import {
  listPurchaseOrders,
  listReceipts,
  getReceipt,
  patchReceipt,
  voidReceipt,
  getReceiptHistory,
  poDtoToUi,
  type ReceiptDto,
  type PurchaseOrderDto,
  type PurchaseOrder,
  type DocumentChangeLogDto,
} from '@/services/procurement'
import { materialService, type Material } from '@/services/materials'
import { CreateMaterialModal } from '@/components/materials/CreateMaterialModal'
import { CreateStockIssueModal } from '@/components/inventory/CreateStockIssueModal'
import { CreateStockInboundModal } from '@/components/inventory/CreateStockInboundModal'
import { StockInboundDetailModal } from '@/components/inventory/StockInboundDetailModal'
import { StockInboundEditModal } from '@/components/inventory/StockInboundEditModal'

function formatQtyWithUom(qty: number, uom?: string | null): string {
  return `${qty.toLocaleString('vi-VN')} ${uom || ''}`.trim()
}

// ─── WAREHOUSE OVERVIEW PAGE ─────────────────────────────────────

type WarehouseOverviewTab = 'materials' | 'free-stock' | 'issue-slips'

interface WarehouseOverviewPageProps {
  forcedTab?: Exclude<WarehouseOverviewTab, 'materials'>
}

export function WarehouseOverviewPage({ forcedTab }: WarehouseOverviewPageProps = {}) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = forcedTab || (searchParams.get('tab') as WarehouseOverviewTab) || 'materials'

  const [convertingFreeItem, setConvertingFreeItem] = useState<FreeStockItem | null>(null)
  const [isConversionHistoryOpen, setIsConversionHistoryOpen] = useState(false)
  const [freeStockRefreshTrigger, setFreeStockRefreshTrigger] = useState(0)

  const handleTabChange = (t: 'materials' | 'free-stock' | 'issue-slips') => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (t === 'materials') next.delete('tab')
      else next.set('tab', t)
      return next
    })
  }

  const canCreateMaterial = useCan('materials.create')
  const [items, setItems] = useState<MaterialStockOverviewItem[]>([])
  const [receiptCount, setReceiptCount] = useState(0)
  const [returnCount, setReturnCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const loadOverview = useCallback(async () => {
    setLoading(true)
    try {
      const [overview, recs, returns] = await Promise.all([
        getStockOverview(),
        listReceipts().catch(() => []),
        listStockMovements({ type: 'RETURN_TO_SUPPLIER' }).catch(() => []),
      ])
      setItems(overview)
      setReceiptCount(recs.length)
      setReturnCount(returns.length)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được dữ liệu kho')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadOverview()
  }, [loadOverview])

  const unresolvedCount = useMemo(
    () => items.filter((i) => i.incoming_unresolved).length,
    [items],
  )

  const lowCount = useMemo(
    () => items.filter((i) => i.available <= 0).length,
    [items],
  )

  const columns: ProtoColumn<MaterialStockOverviewItem>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'Mã',
        sortable: true,
        width: 110,
        cell: (r) => (
          <span className="font-mono text-xs font-semibold">{r.code}</span>
        ),
        sortValue: (r) => r.code,
      },
      {
        id: 'name',
        header: 'Tên',
        sortable: true,
        width: 180,
        cell: (r) => r.name,
        sortValue: (r) => r.name,
      },
      {
        id: 'uom',
        header: 'ĐVT',
        cell: (r) => r.uom_default || '—',
        sortValue: (r) => r.uom_default ?? '',
      },
      {
        id: 'physical',
        header: 'Vật lý',
        align: 'right',
        sortable: true,
        cell: (r) =>
          formatQtyWithUom(
            r.available + r.quarantine + r.blocked + r.damaged + r.rework,
            r.uom_default,
          ),
        sortValue: (r) =>
          r.available + r.quarantine + r.blocked + r.damaged + r.rework,
      },
      {
        id: 'available',
        header: 'Có thể dùng',
        align: 'right',
        sortable: true,
        cell: (r) => (
          <span className="font-semibold text-emerald-800">
            {formatQtyWithUom(r.available, r.uom_default)}
          </span>
        ),
        sortValue: (r) => r.available,
      },
      {
        id: 'incoming',
        header: 'Đang về',
        align: 'right',
        sortable: true,
        cell: (r) => (
          <span className={r.incoming_unresolved ? 'text-amber-700 font-medium' : ''}>
            {formatQtyWithUom(r.incoming_base, r.uom_default)}
            {r.incoming_unresolved ? ' *' : ''}
          </span>
        ),
        sortValue: (r) => r.incoming_base,
      },
      {
        id: 'quarantine',
        header: 'Cách ly (QC)',
        align: 'right',
        sortable: true,
        cell: (r) => formatQtyWithUom(r.quarantine, r.uom_default),
        sortValue: (r) => r.quarantine,
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb items={[{ label: 'Trang chủ', to: '/' }, { label: 'Kho' }]} />
      <PageHeader
        title={
          activeTab === 'free-stock'
            ? 'Kho hàng tự do'
            : activeTab === 'issue-slips'
              ? 'Phiếu xuất kho'
              : 'Tổng quan kho vật tư'
        }
        subtitle={
          activeTab === 'free-stock'
            ? 'Hàng tự do nhận theo PO tự do · Sổ cái kho riêng · Chuyển đổi sang vật tư chuẩn'
            : activeTab === 'issue-slips'
              ? 'Xuất kho tổng hợp vật tư & hàng tự do · Hỗ trợ kéo thả và in A4'
              : 'Bốn số tồn theo vật tư · Postgres SoT · ĐVT của từng dòng'
        }
        actions={
          activeTab === 'materials' ? (
            <div className="flex items-center gap-2">
              {canCreateMaterial ? (
                <Button
                  variant="primary"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  Tạo vật tư
                </Button>
              ) : null}
              <Button
                variant="secondary"
                onClick={() => navigate('/warehouse/stock')}
              >
                Tồn kho chi tiết
              </Button>
            </div>
          ) : null
        }
      />

      {/* Tab Switcher */}
      <div className="mb-4 flex items-center justify-between border-b border-slate-200">
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => handleTabChange('materials')}
            className={`border-b-2 pb-2 text-sm font-semibold transition-colors ${
              activeTab === 'materials'
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Kho vật tư
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('free-stock')}
            className={`border-b-2 pb-2 text-sm font-semibold transition-colors ${
              activeTab === 'free-stock'
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Hàng tự do
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('issue-slips')}
            className={`border-b-2 pb-2 text-sm font-semibold transition-colors ${
              activeTab === 'issue-slips'
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Phiếu xuất kho
          </button>
        </div>
      </div>

      {activeTab === 'free-stock' ? (
        <FreeStockTab
          key={freeStockRefreshTrigger}
          onConvertToMaterial={(item) => setConvertingFreeItem(item)}
          onOpenConversionHistory={() => setIsConversionHistoryOpen(true)}
        />
      ) : activeTab === 'issue-slips' ? (
        <IssueSlipsTab />
      ) : (
        <>
          {error ? <PageError message={error} /> : null}

          {unresolvedCount > 0 ? (
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <b>Chú ý:</b> Có {unresolvedCount} vật tư đang có PO chưa quy đổi được đơn vị (thiếu UOM factor). Số lượng đang về có dấu (*) có thể chưa phản ánh đầy đủ.
            </div>
          ) : null}

          <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
            <KpiCard label="SKU" value={items.length} />
            <KpiCard
              label="Hết / Dưới ngưỡng"
              value={lowCount}
              tone={lowCount ? 'warning' : 'default'}
            />
            <KpiCard label="Phiếu nhập" value={receiptCount} />
            <KpiCard label="Trả hàng" value={returnCount} />
          </div>

          <div className="mb-2 flex flex-wrap gap-2 text-xs">
            <Link
              className="rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 py-1 hover:border-[var(--color-accent)]"
              to="/warehouse/receipts"
            >
              Nhập hàng
            </Link>
            <Link
              className="rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 py-1 hover:border-[var(--color-accent)]"
              to="/warehouse/issues"
            >
              Xuất hàng
            </Link>
            <Link
              className="rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 py-1 hover:border-[var(--color-accent)]"
              to="/warehouse/returns"
            >
              Trả hàng
            </Link>
            <Link
              className="rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2.5 py-1 hover:border-[var(--color-accent)]"
              to="/warehouse/transactions"
            >
              Lịch sử biến động
            </Link>
          </div>

          {loading ? (
            <div className="py-12 text-center text-sm text-slate-400">Đang tải tồn kho...</div>
          ) : (
            <ProtoExcelTable
              columns={columns}
              rows={items}
              rowKey={(r) => r.material_id}
              searchPlaceholder="Tìm mã, tên vật tư..."
              exportName="tong-quan-kho"
              stickyLeftColumnIds={['code', 'name']}
              onRowClick={(r) => navigate(`/warehouse/stock/${r.material_id}`)}
              emptyMessage="Chưa có dữ liệu tồn kho trong hệ thống."
            />
          )}

          <CreateMaterialModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={() => void loadOverview()}
          />
        </>
      )}

      {/* Free stock conversion modals */}
      <FreeConversionModal
        item={convertingFreeItem}
        isOpen={Boolean(convertingFreeItem)}
        onClose={() => setConvertingFreeItem(null)}
        onSuccess={() => {
          setFreeStockRefreshTrigger((c) => c + 1)
          void loadOverview()
        }}
      />

      <FreeConversionHistoryModal
        isOpen={isConversionHistoryOpen}
        onClose={() => setIsConversionHistoryOpen(false)}
        onConversionChanged={() => {
          setFreeStockRefreshTrigger((c) => c + 1)
          void loadOverview()
        }}
      />
    </div>
  )
}

export function FreeStockPage() {
  return <WarehouseOverviewPage forcedTab="free-stock" />
}

export function IssueSlipsPage() {
  return <WarehouseOverviewPage forcedTab="issue-slips" />
}

// ─── RECEIPTS MODALS ─────────────────────────────────────────────

interface ReceiptEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (lines: Array<{ id: string; qty: number }>, reason: string, note?: string) => Promise<void>
  receipt: ReceiptDto | null
  loading?: boolean
}

function ReceiptEditModal({
  isOpen,
  onClose,
  onSave,
  receipt,
  loading = false,
}: ReceiptEditModalProps) {
  const [lineQtys, setLineQtys] = useState<Record<string, number>>({})
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (isOpen && receipt?.lines) {
      const initial: Record<string, number> = {}
      for (const l of receipt.lines) {
        initial[l.id] = Number(l.qty_order_uom)
      }
      setLineQtys(initial)
      setReason('')
      setNote(receipt.notes ?? '')
    }
  }, [isOpen, receipt])

  if (!isOpen || !receipt) return null

  const isInvalid = !reason.trim() || Object.values(lineQtys).some((q) => q < 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Edit2 className="h-5 w-5 text-emerald-700" />
            <span>Chỉnh sửa phiếu nhập: {receipt.code}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs max-h-[70vh] overflow-y-auto">
          <p className="text-slate-600 font-medium mb-2">Số lượng nhận theo từng dòng:</p>
          {(receipt.lines ?? []).map((l) => {
            const currentVal = lineQtys[l.id] ?? Number(l.qty_order_uom)
            const oldVal = Number(l.qty_order_uom)
            const delta = Number((currentVal - oldVal).toFixed(4))
            return (
              <div key={l.id} className="rounded border border-slate-200 p-3 bg-slate-50/50">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="font-mono font-semibold text-slate-800">Dòng PO: {l.po_line_id}</span>
                  <span className="text-slate-500">Đã nhận: {oldVal} {l.order_uom}</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-slate-700 font-medium">SL mới:</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={currentVal}
                    onChange={(e) =>
                      setLineQtys({ ...lineQtys, [l.id]: Number(e.target.value) })
                    }
                    disabled={loading}
                    className="w-32 rounded border border-[var(--color-border)] bg-white p-1.5 text-xs font-mono"
                  />
                  <span className="text-slate-600">{l.order_uom}</span>
                </div>
                {delta !== 0 && (
                  <div className={`mt-2 text-xs font-medium ${delta > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
                    Tồn kho sẽ {delta > 0 ? 'tăng' : 'giảm'} {Math.abs(delta)} ({l.order_uom})
                  </div>
                )}
              </div>
            )
          })}

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Lý do chỉnh sửa <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VD: Kiểm đếm lại thực tế tại kho..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">Ghi chú phiếu</label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú thêm..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={isInvalid || loading}
              onClick={async () => {
                const changedLines = Object.entries(lineQtys).map(([id, qty]) => ({ id, qty }))
                await onSave(changedLines, reason.trim(), note || undefined)
              }}
            >
              Lưu thay đổi
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ReceiptDetailModalProps {
  isOpen: boolean
  onClose: () => void
  receipt: ReceiptDto | null
  canWrite: boolean
  onEdit: () => void
  onVoid: () => void
}

function ReceiptDetailModal({
  isOpen,
  onClose,
  receipt,
  canWrite,
  onEdit,
  onVoid,
}: ReceiptDetailModalProps) {
  const [tab, setTab] = useState<'lines' | 'history'>('lines')
  const [historyLogs, setHistoryLogs] = useState<DocumentChangeLogDto[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && tab === 'history' && receipt?.id) {
      setHistoryLoading(true)
      setHistoryError(null)
      getReceiptHistory(receipt.id)
        .then(setHistoryLogs)
        .catch((err) => setHistoryError(err instanceof Error ? err.message : 'Lỗi tải lịch sử'))
        .finally(() => setHistoryLoading(false))
    }
  }, [isOpen, tab, receipt?.id])

  if (!isOpen || !receipt) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-base">
              <span>Phiếu nhập kho: {receipt.code}</span>
              {receipt.is_void ? (
                <Badge tone="danger">Đã hủy</Badge>
              ) : (
                <Badge tone="success">Hoạt động</Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              PO: {receipt.po_code || receipt.po_id} · Người nhận: {receipt.received_by} · Ngày:{' '}
              <span title={receipt.created_at ? formatDateFull(receipt.created_at) : undefined}>
                {receipt.created_at ? formatDate(receipt.created_at) : '—'}
              </span>
            </p>
            {receipt.is_void && receipt.void_reason && (
              <p className="text-xs text-red-600 mt-1 font-medium">
                Lý do hủy: {receipt.void_reason} (bởi {receipt.voided_by || '—'})
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex rounded-[var(--radius-sm)] border border-[var(--color-border)] p-0.5">
            <button
              type="button"
              className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                tab === 'lines'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:bg-slate-50'
              }`}
              onClick={() => setTab('lines')}
            >
              Dòng hàng
            </button>
            <button
              type="button"
              className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                tab === 'history'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:bg-slate-50'
              }`}
              onClick={() => setTab('history')}
            >
              Lịch sử thay đổi
            </button>
          </div>

          {!receipt.is_void && canWrite && (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={onEdit}>
                <Edit2 className="mr-1 h-3.5 w-3.5" />
                Sửa phiếu
              </Button>
              <Button size="sm" variant="ghost" onClick={onVoid} className="text-red-600 hover:bg-red-50">
                <Ban className="mr-1 h-3.5 w-3.5" />
                Hủy phiếu
              </Button>
            </div>
          )}
        </div>

        <div className="mt-3 max-h-[60vh] overflow-y-auto">
          {tab === 'lines' ? (
            <div className="rounded border border-slate-100 overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-2">Dòng PO</th>
                    <th className="p-2 text-right">SL đặt nhận</th>
                    <th className="p-2">ĐVT đặt</th>
                    <th className="p-2 text-right">SL quy đổi gốc</th>
                    <th className="p-2">ĐVT gốc</th>
                    <th className="p-2">Ghi chú</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(receipt.lines ?? []).map((l) => (
                    <tr key={l.id}>
                      <td className="p-2 font-mono">{l.po_line_id}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{l.qty_order_uom}</td>
                      <td className="p-2">{l.order_uom}</td>
                      <td className="p-2 text-right tabular-nums">{l.qty_base ?? '—'}</td>
                      <td className="p-2">{l.base_uom}</td>
                      <td className="p-2 text-slate-500">{l.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <DocumentHistoryTab
              logs={historyLogs}
              loading={historyLoading}
              error={historyError}
              emptyMessage="Chưa có lịch sử thay đổi cho phiếu nhập này."
            />
          )}
        </div>

        <div className="mt-4 flex justify-end border-t border-slate-100 pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── RECEIPTS PAGE ───────────────────────────────────────────────

export function ReceiptsPage() {
  const navigate = useNavigate()
  const canWriteReceiving = useCan('receiving.write')
  const [activeTab, setActiveTab] = useState<'po' | 'direct'>('po')
  const [pos, setPos] = useState<PurchaseOrderDto[]>([])
  const [receipts, setReceipts] = useState<ReceiptDto[]>([])
  const [inbounds, setInbounds] = useState<StockInboundDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  // PO Receipt modals state
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptDto | null>(null)
  const [receiptDetailOpen, setReceiptDetailOpen] = useState(false)
  const [receiptEditOpen, setReceiptEditOpen] = useState(false)
  const [receiptVoidOpen, setReceiptVoidOpen] = useState(false)

  // Direct Inbound modals state
  const [selectedInbound, setSelectedInbound] = useState<StockInboundDto | null>(null)
  const [inboundCreateOpen, setInboundCreateOpen] = useState(false)
  const [inboundDetailOpen, setInboundDetailOpen] = useState(false)
  const [inboundEditOpen, setInboundEditOpen] = useState(false)
  const [inboundVoidOpen, setInboundVoidOpen] = useState(false)

  const [actionLoading, setActionLoading] = useState(false)

  async function loadData() {
    try {
      const [poList, recList, inbList] = await Promise.all([
        listPurchaseOrders(),
        listReceipts(),
        listStockInbounds(),
      ])
      setPos(poList)
      setReceipts(recList)
      setInbounds(inbList)
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'Không tải được danh sách phiếu nhập')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [])

  // ─── PO Receipt Handlers ───
  async function handleOpenReceipt(id: string) {
    setError(null)
    try {
      const full = await getReceipt(id)
      setSelectedReceipt(full)
      setReceiptDetailOpen(true)
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    }
  }

  async function handleSaveReceipt(
    lines: Array<{ id: string; qty: number }>,
    reason: string,
    note?: string,
  ) {
    if (!selectedReceipt) return
    setActionLoading(true)
    setError(null)
    try {
      const updated = await patchReceipt(selectedReceipt.id, { lines, notes: note }, reason)
      setSelectedReceipt(updated)
      setReceiptEditOpen(false)
      setMessage(`Đã cập nhật phiếu nhập ${updated.code} thành công.`)
      await loadData()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setActionLoading(false)
    }
  }

  async function handleVoidReceipt(reason: string) {
    if (!selectedReceipt) return
    setActionLoading(true)
    setError(null)
    try {
      const updated = await voidReceipt(selectedReceipt.id, reason)
      setSelectedReceipt(updated)
      setReceiptVoidOpen(false)
      setMessage(`Đã hủy phiếu nhập ${updated.code} thành công.`)
      await loadData()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setActionLoading(false)
    }
  }

  // ─── Direct Inbound Handlers ───
  async function handleOpenInbound(id: string) {
    setError(null)
    try {
      const full = await getStockInbound(id)
      setSelectedInbound(full)
      setInboundDetailOpen(true)
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    }
  }

  async function handleSaveInbound(
    lines: Array<{ id: string; qty_base: number }>,
    source: StockInboundSource,
    note?: string,
    reason?: string,
  ) {
    if (!selectedInbound) return
    setActionLoading(true)
    setError(null)
    try {
      const updated = await patchStockInbound(selectedInbound.id, {
        source,
        note: reason ? `${reason}${note ? ` (${note})` : ''}` : note,
        lines,
      })
      setSelectedInbound(updated)
      setInboundEditOpen(false)
      setMessage(`Đã cập nhật phiếu nhập trực tiếp ${updated.code} thành công.`)
      await loadData()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setActionLoading(false)
    }
  }

  async function handleVoidInbound(reason: string) {
    if (!selectedInbound) return
    setActionLoading(true)
    setError(null)
    try {
      const updated = await voidStockInbound(selectedInbound.id, reason)
      setSelectedInbound(updated)
      setInboundVoidOpen(false)
      setMessage(`Đã hủy phiếu nhập trực tiếp ${updated.code} thành công.`)
      await loadData()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setActionLoading(false)
    }
  }

  const uiPos: PurchaseOrder[] = useMemo(() => pos.map(poDtoToUi), [pos])
  const openPos = useMemo(
    () => uiPos.filter((p) => p.status === 'SENT' || p.status === 'PARTIAL'),
    [uiPos],
  )

  const openPoColumns: ProtoColumn<PurchaseOrder>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'Mã PO',
        sortable: true,
        width: 120,
        cell: (po) => (
          <span className="font-mono text-xs font-semibold text-[var(--color-link)]">
            {po.code}
          </span>
        ),
        sortValue: (po) => po.code,
      },
      {
        id: 'items',
        header: 'Vật tư',
        cell: (po) => po.lines.map((l) => l.itemCode).join(', '),
        sortValue: (po) => po.lines.map((l) => l.itemCode).join(', '),
      },
      {
        id: 'progress',
        header: 'Tiến độ',
        align: 'right',
        cell: (po) => (
          <span className="tabular-nums">
            {formatReceivePct(poReceiveProgress(po))}
          </span>
        ),
        sortValue: (po) => poReceiveProgress(po),
      },
      {
        id: 'status',
        header: 'TT',
        cell: (po) => <PoStatusBadge status={po.status} />,
        sortValue: (po) => po.status,
      },
    ],
    [],
  )

  const receiptColumns: ProtoColumn<ReceiptDto>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'Mã phiếu',
        sortable: true,
        width: 120,
        cell: (r) => (
          <span className="font-mono text-xs font-semibold">{r.code}</span>
        ),
        sortValue: (r) => r.code,
      },
      {
        id: 'po',
        header: 'PO',
        cell: (r) => r.po_code || r.po_id,
        sortValue: (r) => r.po_code || r.po_id,
      },
      {
        id: 'status',
        header: 'Trạng thái',
        cell: (r) =>
          r.is_void ? (
            <Badge tone="danger" className="text-xs font-medium">
              Đã hủy
            </Badge>
          ) : (
            <Badge tone="success" className="text-xs font-medium">
              Hoạt động
            </Badge>
          ),
        sortValue: (r) => (r.is_void ? 'VOID' : 'ACTIVE'),
      },
      {
        id: 'at',
        header: 'Ngày',
        sortable: true,
        cell: (r) =>
          r.created_at ? (
            <span title={formatDateFull(r.created_at)}>{formatDate(r.created_at)}</span>
          ) : (
            '—'
          ),
        sortValue: (r) => r.created_at,
      },
      {
        id: 'by',
        header: 'Người nhận',
        cell: (r) => r.received_by,
        sortValue: (r) => r.received_by,
      },
    ],
    [],
  )

  const inboundColumns: ProtoColumn<StockInboundDto>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'Mã phiếu',
        sortable: true,
        width: 140,
        cell: (inb) => (
          <span className="font-mono text-xs font-semibold text-[var(--color-link)]">
            {inb.code}
          </span>
        ),
        sortValue: (inb) => inb.code,
      },
      {
        id: 'source',
        header: 'Nguồn nhập',
        cell: (inb) => (
          <span className="font-medium text-slate-700">
            {STOCK_INBOUND_SOURCE_LABELS[inb.source] || inb.source}
          </span>
        ),
        sortValue: (inb) => inb.source,
      },
      {
        id: 'status',
        header: 'Trạng thái',
        cell: (inb) =>
          inb.status === 'VOID' ? (
            <Badge tone="danger" className="text-xs font-medium">
              Đã hủy
            </Badge>
          ) : (
            <Badge tone="success" className="text-xs font-medium">
              Hoạt động
            </Badge>
          ),
        sortValue: (inb) => inb.status,
      },
      {
        id: 'at',
        header: 'Ngày tạo',
        sortable: true,
        cell: (inb) =>
          inb.created_at ? (
            <span title={formatDateFull(inb.created_at)}>{formatDate(inb.created_at)}</span>
          ) : (
            '—'
          ),
        sortValue: (inb) => inb.created_at,
      },
      {
        id: 'by',
        header: 'Người tạo',
        cell: (inb) => inb.created_by,
        sortValue: (inb) => inb.created_by,
      },
      {
        id: 'note',
        header: 'Ghi chú',
        cell: (inb) => inb.note || '—',
        sortValue: (inb) => inb.note || '',
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Kho', to: '/warehouse' },
          { label: 'Nhập hàng' },
        ]}
      />
      <PageHeader
        title="Nhận & Nhập hàng"
        subtitle="Quản lý tiếp nhận hàng theo PO và các phiếu nhập kho trực tiếp (đầu kỳ, mua lẻ, ngoài PO)."
      />

      {error ? <PageError message={error} /> : null}
      {message ? (
        <p className="mb-3 rounded-[var(--radius-md)] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {message}
        </p>
      ) : null}

      {/* Tab Switcher */}
      <div className="mb-4 flex items-center justify-between border-b border-slate-200">
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setActiveTab('po')}
            className={`border-b-2 pb-2 text-sm font-semibold transition-colors ${
              activeTab === 'po'
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Nhận theo PO
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('direct')}
            className={`border-b-2 pb-2 text-sm font-semibold transition-colors ${
              activeTab === 'direct'
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Nhập kho trực tiếp
          </button>
        </div>
        {activeTab === 'direct' && canWriteReceiving && (
          <div className="pb-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setInboundCreateOpen(true)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Tạo phiếu nhập trực tiếp
            </Button>
          </div>
        )}
      </div>

      {activeTab === 'po' ? (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
            <KpiCard label="PO chờ nhận" value={openPos.length} />
            <KpiCard label="Phiếu đã ghi" value={receipts.length} />
          </div>

          <h2 className="mb-2 text-sm font-semibold">PO chờ nhận</h2>
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Đang tải...</div>
          ) : openPos.length === 0 ? (
            <EmptyState
              title="Không có PO chờ nhận"
              description="Chỉ hiện PO SENT / PARTIAL."
            />
          ) : (
            <div className="mb-6">
              <ProtoExcelTable
                columns={openPoColumns}
                rows={openPos}
                rowKey={(r) => r.id}
                exportName="po-cho-nhan"
                stickyLeftColumnIds={['code']}
                onRowClick={(po) => navigate(`/purchasing/po/${po.id}`)}
                emptyMessage="Không có PO chờ nhận."
              />
            </div>
          )}

          <h2 className="mb-2 text-sm font-semibold">Phiếu đã ghi</h2>
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Đang tải...</div>
          ) : receipts.length === 0 ? (
            <EmptyState
              title="Chưa có phiếu nhập"
              description="Nhận hàng từ màn chi tiết PO."
            />
          ) : (
            <ProtoExcelTable
              columns={receiptColumns}
              rows={receipts}
              rowKey={(r) => r.id}
              exportName="phieu-nhap"
              stickyLeftColumnIds={['code']}
              onRowClick={(r) => void handleOpenReceipt(r.id)}
              emptyMessage="Chưa có phiếu nhập."
            />
          )}

          <ReceiptDetailModal
            isOpen={receiptDetailOpen}
            onClose={() => setReceiptDetailOpen(false)}
            receipt={selectedReceipt}
            canWrite={canWriteReceiving}
            onEdit={() => setReceiptEditOpen(true)}
            onVoid={() => setReceiptVoidOpen(true)}
          />

          <ReceiptEditModal
            isOpen={receiptEditOpen}
            onClose={() => setReceiptEditOpen(false)}
            onSave={handleSaveReceipt}
            receipt={selectedReceipt}
            loading={actionLoading}
          />

          <VoidDocumentDialog
            isOpen={receiptVoidOpen}
            documentCode={selectedReceipt?.code ?? ''}
            stockImpactWarning="Hủy phiếu nhập kho sẽ trừ giảm lượng tồn kho tương ứng đã nhập và khôi phục trạng thái nhận hàng trên PO."
            onClose={() => setReceiptVoidOpen(false)}
            onConfirm={handleVoidReceipt}
          />
        </>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
            <KpiCard label="Tổng phiếu nhập trực tiếp" value={inbounds.length} />
            <KpiCard
              label="Phiếu hoạt động"
              value={inbounds.filter((i) => i.status === 'ACTIVE').length}
            />
            <KpiCard
              label="Phiếu đã hủy"
              value={inbounds.filter((i) => i.status === 'VOID').length}
            />
          </div>

          <h2 className="mb-2 text-sm font-semibold">Danh sách phiếu nhập trực tiếp</h2>
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-400">Đang tải...</div>
          ) : inbounds.length === 0 ? (
            <EmptyState
              title="Chưa có phiếu nhập trực tiếp"
              description="Bấm '+ Tạo phiếu nhập trực tiếp' để tạo phiếu đầu tiên."
            />
          ) : (
            <ProtoExcelTable
              columns={inboundColumns}
              rows={inbounds}
              rowKey={(r) => r.id}
              exportName="phieu-nhap-truc-tiep"
              stickyLeftColumnIds={['code']}
              onRowClick={(inb) => void handleOpenInbound(inb.id)}
              emptyMessage="Chưa có phiếu nhập trực tiếp."
            />
          )}

          <CreateStockInboundModal
            isOpen={inboundCreateOpen}
            onClose={() => setInboundCreateOpen(false)}
            onSuccess={(code) => {
              setMessage(`Đã tạo phiếu nhập kho trực tiếp ${code} thành công.`)
              void loadData()
            }}
          />

          <StockInboundDetailModal
            isOpen={inboundDetailOpen}
            onClose={() => setInboundDetailOpen(false)}
            inbound={selectedInbound}
            canWrite={canWriteReceiving}
            onEdit={() => setInboundEditOpen(true)}
            onVoid={() => setInboundVoidOpen(true)}
          />

          <StockInboundEditModal
            isOpen={inboundEditOpen}
            onClose={() => setInboundEditOpen(false)}
            onSave={handleSaveInbound}
            inbound={selectedInbound}
            loading={actionLoading}
          />

          <VoidDocumentDialog
            isOpen={inboundVoidOpen}
            documentCode={selectedInbound?.code ?? ''}
            stockImpactWarning="Hủy phiếu nhập kho sẽ trừ giảm lượng tồn kho tương ứng đã nhập trước đó. Nếu tồn kho không đủ, hệ thống sẽ từ chối."
            onClose={() => setInboundVoidOpen(false)}
            onConfirm={handleVoidInbound}
          />
        </>
      )}
    </div>
  )
}

// ─── STOCK ISSUES MODALS ─────────────────────────────────────────

interface StockIssueEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (
    lines: Array<{ id: string; qty_base: number; note?: string }>,
    reason: string,
    purpose?: string,
    note?: string,
  ) => Promise<void>
  issue: StockIssueDto | null
  loading?: boolean
}

function StockIssueEditModal({
  isOpen,
  onClose,
  onSave,
  issue,
  loading = false,
}: StockIssueEditModalProps) {
  const [lineQtys, setLineQtys] = useState<Record<string, number>>({})
  const [purpose, setPurpose] = useState('')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (isOpen && issue?.lines) {
      const initial: Record<string, number> = {}
      for (const l of issue.lines) {
        initial[l.id] = Number(l.qty_base)
      }
      setLineQtys(initial)
      setPurpose(issue.purpose ?? '')
      setNote(issue.note ?? '')
      setReason('')
    }
  }, [isOpen, issue])

  if (!isOpen || !issue) return null

  const isInvalid = !reason.trim() || Object.values(lineQtys).some((q) => q <= 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Edit2 className="h-5 w-5 text-emerald-700" />
            <span>Chỉnh sửa phiếu xuất kho: {issue.code}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block font-medium text-slate-700 mb-1">Mục đích xuất</label>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <p className="text-slate-600 font-medium mb-1">Số lượng xuất theo từng dòng:</p>
          {(issue.lines ?? []).map((l) => {
            const currentVal = lineQtys[l.id] ?? Number(l.qty_base)
            const oldVal = Number(l.qty_base)
            const delta = Number((currentVal - oldVal).toFixed(4))
            return (
              <div key={l.id} className="rounded border border-slate-200 p-3 bg-slate-50/50">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="font-mono font-semibold text-slate-800">{l.material_code || l.material_id}</span>
                  <span className="text-slate-500">Đã xuất: {oldVal} {l.base_uom}</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-slate-700 font-medium">SL mới:</label>
                  <input
                    type="number"
                    min="0.0001"
                    step="any"
                    value={currentVal}
                    onChange={(e) =>
                      setLineQtys({ ...lineQtys, [l.id]: Number(e.target.value) })
                    }
                    disabled={loading}
                    className="w-32 rounded border border-[var(--color-border)] bg-white p-1.5 text-xs font-mono"
                  />
                  <span className="text-slate-600">{l.base_uom}</span>
                </div>
                {delta !== 0 && (
                  <div className={`mt-2 text-xs font-medium ${delta > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
                    Tồn kho sẽ {delta > 0 ? 'giảm thêm' : 'tăng hoàn lại'} {Math.abs(delta)} ({l.base_uom})
                  </div>
                )}
              </div>
            )
          })}

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Lý do chỉnh sửa <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VD: Cập nhật lại định mức xuất thực tế..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">Ghi chú phiếu</label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú thêm..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={isInvalid || loading}
              onClick={async () => {
                const changedLines = Object.entries(lineQtys).map(([id, qty_base]) => ({ id, qty_base }))
                await onSave(changedLines, reason.trim(), purpose.trim() || undefined, note || undefined)
              }}
            >
              Lưu thay đổi
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface StockIssueDetailModalProps {
  isOpen: boolean
  onClose: () => void
  issue: StockIssueDto | null
  canWrite: boolean
  onEdit: () => void
  onVoid: () => void
}

function StockIssueDetailModal({
  isOpen,
  onClose,
  issue,
  canWrite,
  onEdit,
  onVoid,
}: StockIssueDetailModalProps) {
  const [tab, setTab] = useState<'lines' | 'history'>('lines')
  const [historyLogs, setHistoryLogs] = useState<DocumentChangeLogDto[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && tab === 'history' && issue?.id) {
      setHistoryLoading(true)
      setHistoryError(null)
      getStockIssueHistory(issue.id)
        .then(setHistoryLogs)
        .catch((err) => setHistoryError(err instanceof Error ? err.message : 'Lỗi tải lịch sử'))
        .finally(() => setHistoryLoading(false))
    }
  }, [isOpen, tab, issue?.id])

  if (!isOpen || !issue) return null

  const isVoid = issue.status === 'VOID' || issue.is_void

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-base">
              <span>Phiếu xuất kho: {issue.code}</span>
              {isVoid ? (
                <Badge tone="danger">Đã hủy</Badge>
              ) : (
                <Badge tone="success">Hoạt động</Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Mục đích: {issue.purpose} · Người tạo: {issue.created_by} · Ngày:{' '}
              <span title={issue.created_at ? formatDateFull(issue.created_at) : undefined}>
                {issue.created_at ? formatDate(issue.created_at) : '—'}
              </span>
            </p>
            {isVoid && issue.void_reason && (
              <p className="text-xs text-red-600 mt-1 font-medium">
                Lý do hủy: {issue.void_reason} (bởi {issue.voided_by || '—'})
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex rounded-[var(--radius-sm)] border border-[var(--color-border)] p-0.5">
            <button
              type="button"
              className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                tab === 'lines'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:bg-slate-50'
              }`}
              onClick={() => setTab('lines')}
            >
              Dòng hàng
            </button>
            <button
              type="button"
              className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                tab === 'history'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:bg-slate-50'
              }`}
              onClick={() => setTab('history')}
            >
              Lịch sử thay đổi
            </button>
          </div>

          {!isVoid && canWrite && (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={onEdit}>
                <Edit2 className="mr-1 h-3.5 w-3.5" />
                Sửa phiếu
              </Button>
              <Button size="sm" variant="ghost" onClick={onVoid} className="text-red-600 hover:bg-red-50">
                <Ban className="mr-1 h-3.5 w-3.5" />
                Hủy phiếu
              </Button>
            </div>
          )}
        </div>

        <div className="mt-3 max-h-[60vh] overflow-y-auto">
          {tab === 'lines' ? (
            <div className="rounded border border-slate-100 overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-2">Vật tư</th>
                    <th className="p-2 text-right">SL xuất</th>
                    <th className="p-2">ĐVT gốc</th>
                    <th className="p-2">Ghi chú</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(issue.lines ?? []).map((l) => (
                    <tr key={l.id}>
                      <td className="p-2 font-mono font-medium">{l.material_code || l.material_id}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{l.qty_base}</td>
                      <td className="p-2">{l.base_uom}</td>
                      <td className="p-2 text-slate-500">{l.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <DocumentHistoryTab
              logs={historyLogs}
              loading={historyLoading}
              error={historyError}
              emptyMessage="Chưa có lịch sử thay đổi cho phiếu xuất kho này."
            />
          )}
        </div>

        <div className="mt-4 flex justify-end border-t border-slate-100 pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── ISSUES PAGE ─────────────────────────────────────────────────

export function IssuesPage() {
  const canWriteInventory = useCan('inventory.write')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [createIssueModalOpen, setCreateIssueModalOpen] = useState(false)

  const [issueMovements, setIssueMovements] = useState<StockMovementDto[]>([])
  const [movementsLoading, setMovementsLoading] = useState(true)

  const [selectedIssue, setSelectedIssue] = useState<StockIssueDto | null>(null)
  const [issueDetailOpen, setIssueDetailOpen] = useState(false)
  const [issueEditOpen, setIssueEditOpen] = useState(false)
  const [issueVoidOpen, setIssueVoidOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  async function loadData() {
    try {
      const movs = await listStockMovements({ type: 'ISSUE' })
      setIssueMovements(movs)
    } catch {
      // ignore
    } finally {
      setMovementsLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [])

  async function handleOpenIssue(id: string) {
    setError(null)
    try {
      const full = await getStockIssue(id)
      if (full) {
        setSelectedIssue(full)
        setIssueDetailOpen(true)
      }
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    }
  }

  async function handleSaveIssue(
    lines: Array<{ id: string; qty_base: number; note?: string }>,
    reason: string,
    newPurpose?: string,
    newNote?: string,
  ) {
    if (!selectedIssue) return
    setActionLoading(true)
    setError(null)
    try {
      const updated = await patchStockIssue(
        selectedIssue.id,
        { lines, purpose: newPurpose, note: newNote },
        reason,
      )
      setSelectedIssue(updated)
      setIssueEditOpen(false)
      setMessage(`Đã cập nhật phiếu xuất kho ${updated.code} thành công.`)
      await loadData()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setActionLoading(false)
    }
  }

  async function handleVoidIssue(reason: string) {
    if (!selectedIssue) return
    setActionLoading(true)
    setError(null)
    try {
      const updated = await voidStockIssue(selectedIssue.id, reason)
      setSelectedIssue(updated)
      setIssueVoidOpen(false)
      setMessage(`Đã hủy phiếu xuất kho ${updated.code} thành công.`)
      await loadData()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setActionLoading(false)
    }
  }

  const columns: ProtoColumn<StockMovementDto>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'Mã chứng từ',
        sortable: true,
        width: 130,
        cell: (r) => (
          <span className="font-mono text-xs font-semibold text-[var(--color-link)]">
            {r.doc_code || '—'}
          </span>
        ),
        sortValue: (r) => r.doc_code || '',
      },
      {
        id: 'mat',
        header: 'Vật tư',
        cell: (r) => r.material_code || r.material_id,
        sortValue: (r) => r.material_code || r.material_id,
      },
      {
        id: 'qty',
        header: 'Số lượng xuất',
        align: 'right',
        cell: (r) => (
          <span className="tabular-nums font-semibold text-amber-800">
            {formatQtyWithUom(Math.abs(r.qty_base), r.base_uom)}
          </span>
        ),
        sortValue: (r) => Math.abs(r.qty_base),
      },
      {
        id: 'at',
        header: 'Ngày',
        sortable: true,
        cell: (r) =>
          r.at ? (
            <span title={formatDateFull(r.at)}>{formatDate(r.at)}</span>
          ) : (
            '—'
          ),
        sortValue: (r) => r.at,
      },
      {
        id: 'by',
        header: 'Người xuất',
        cell: (r) => r.by || '—',
        sortValue: (r) => r.by || '',
      },
      {
        id: 'note',
        header: 'Ghi chú',
        cell: (r) => r.note || '—',
        sortValue: (r) => r.note || '',
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Kho', to: '/warehouse' },
          { label: 'Xuất hàng' },
        ]}
      />
      <PageHeader
        title="Xuất hàng"
        subtitle="Ghi nhận xuất kho vật tư vào sổ kho Postgres SoT · Xem và chỉnh sửa lịch sử xuất kho."
        actions={
          canWriteInventory ? (
            <Button
              variant="primary"
              onClick={() => setCreateIssueModalOpen(true)}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Tạo phiếu xuất kho
            </Button>
          ) : null
        }
      />

      {error ? <PageError message={error} /> : null}
      {message ? (
        <p className="mb-3 rounded-[var(--radius-md)] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {message}
        </p>
      ) : null}

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Giao dịch xuất kho" value={issueMovements.length} />
      </div>

      {canWriteInventory ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Tạo phiếu xuất kho</h3>
            <p className="text-xs text-[var(--color-text-muted)]">
              Hỗ trợ xuất nhiều vật tư trong một chứng từ · Tự động kiểm tra và chặn xuất vượt tồn khả dụng.
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => setCreateIssueModalOpen(true)}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Tạo phiếu xuất kho (nhiều vật tư)
          </Button>
        </div>
      ) : null}

      <h3 className="text-sm font-semibold mb-2 text-slate-800">Danh sách phiếu xuất kho đã ghi nhận</h3>
      {movementsLoading ? (
        <div className="py-8 text-center text-sm text-slate-400">Đang tải phiếu xuất kho...</div>
      ) : issueMovements.length === 0 ? (
        <EmptyState title="Chưa có phiếu xuất kho" description="Tạo phiếu xuất kho bằng nút phía trên." />
      ) : (
        <ProtoExcelTable
          columns={columns}
          rows={issueMovements}
          rowKey={(r) => r.id}
          exportName="phieu-xuat-kho"
          stickyLeftColumnIds={['code']}
          onRowClick={(r) => {
            if (r.doc_id) {
              void handleOpenIssue(r.doc_id)
            }
          }}
          emptyMessage="Chưa có phiếu xuất kho."
        />
      )}

      <CreateStockIssueModal
        isOpen={createIssueModalOpen}
        onClose={() => setCreateIssueModalOpen(false)}
        onSuccess={(code) => {
          setMessage(`Đã xuất kho thành công · Mã phiếu ${code}.`)
          void loadData()
        }}
      />

      <StockIssueDetailModal
        isOpen={issueDetailOpen}
        onClose={() => setIssueDetailOpen(false)}
        issue={selectedIssue}
        canWrite={canWriteInventory}
        onEdit={() => setIssueEditOpen(true)}
        onVoid={() => setIssueVoidOpen(true)}
      />

      <StockIssueEditModal
        isOpen={issueEditOpen}
        onClose={() => setIssueEditOpen(false)}
        onSave={handleSaveIssue}
        issue={selectedIssue}
        loading={actionLoading}
      />

      <VoidDocumentDialog
        isOpen={issueVoidOpen}
        documentCode={selectedIssue?.code ?? ''}
        stockImpactWarning="Hủy phiếu xuất kho sẽ hoàn trả lại toàn bộ số lượng vật tư tương ứng về tồn kho khả dụng."
        onClose={() => setIssueVoidOpen(false)}
        onConfirm={handleVoidIssue}
      />
    </div>
  )
}

// ─── SUPPLIER RETURNS MODALS ─────────────────────────────────────

interface SupplierReturnEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (
    lines: Array<{ id: string; qty_base: number; note?: string }>,
    auditReason: string,
    reason?: string,
    note?: string,
  ) => Promise<void>
  supplierReturn: SupplierReturnDto | null
  loading?: boolean
}

function SupplierReturnEditModal({
  isOpen,
  onClose,
  onSave,
  supplierReturn,
  loading = false,
}: SupplierReturnEditModalProps) {
  const [lineQtys, setLineQtys] = useState<Record<string, number>>({})
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [auditReason, setAuditReason] = useState('')

  useEffect(() => {
    if (isOpen && supplierReturn?.lines) {
      const initial: Record<string, number> = {}
      for (const l of supplierReturn.lines) {
        initial[l.id] = Number(l.qty_base)
      }
      setLineQtys(initial)
      setReason(supplierReturn.reason ?? '')
      setNote(supplierReturn.note ?? '')
      setAuditReason('')
    }
  }, [isOpen, supplierReturn])

  if (!isOpen || !supplierReturn) return null

  const isInvalid = !auditReason.trim() || Object.values(lineQtys).some((q) => q <= 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Edit2 className="h-5 w-5 text-emerald-700" />
            <span>Chỉnh sửa phiếu trả NCC: {supplierReturn.code}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block font-medium text-slate-700 mb-1">Lý do trả hàng</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <p className="text-slate-600 font-medium mb-1">Số lượng trả theo từng dòng:</p>
          {(supplierReturn.lines ?? []).map((l) => {
            const currentVal = lineQtys[l.id] ?? Number(l.qty_base)
            const oldVal = Number(l.qty_base)
            const delta = Number((currentVal - oldVal).toFixed(4))
            return (
              <div key={l.id} className="rounded border border-slate-200 p-3 bg-slate-50/50">
                <div className="flex justify-between items-center mb-1.5">
                  <span className="font-mono font-semibold text-slate-800">{l.material_code || l.material_id}</span>
                  <span className="text-slate-500">Đã trả: {oldVal} {l.base_uom}</span>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-slate-700 font-medium">SL mới:</label>
                  <input
                    type="number"
                    min="0.0001"
                    step="any"
                    value={currentVal}
                    onChange={(e) =>
                      setLineQtys({ ...lineQtys, [l.id]: Number(e.target.value) })
                    }
                    disabled={loading}
                    className="w-32 rounded border border-[var(--color-border)] bg-white p-1.5 text-xs font-mono"
                  />
                  <span className="text-slate-600">{l.base_uom}</span>
                </div>
                {delta !== 0 && (
                  <div className={`mt-2 text-xs font-medium ${delta > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                    <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
                    Tồn kho sẽ {delta > 0 ? 'giảm thêm' : 'tăng hoàn lại'} {Math.abs(delta)} ({l.base_uom})
                  </div>
                )}
              </div>
            )
          })}

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Lý do chỉnh sửa (Ghi log kiểm toán) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={auditReason}
              onChange={(e) => setAuditReason(e.target.value)}
              placeholder="VD: Cập nhật lại số lượng trả theo biên bản nghiệm thu..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">Ghi chú phiếu</label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú thêm..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={isInvalid || loading}
              onClick={async () => {
                const changedLines = Object.entries(lineQtys).map(([id, qty_base]) => ({ id, qty_base }))
                await onSave(changedLines, auditReason.trim(), reason.trim() || undefined, note || undefined)
              }}
            >
              Lưu thay đổi
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface SupplierReturnDetailModalProps {
  isOpen: boolean
  onClose: () => void
  supplierReturn: SupplierReturnDto | null
  canWrite: boolean
  onEdit: () => void
  onVoid: () => void
}

function SupplierReturnDetailModal({
  isOpen,
  onClose,
  supplierReturn,
  canWrite,
  onEdit,
  onVoid,
}: SupplierReturnDetailModalProps) {
  const [tab, setTab] = useState<'lines' | 'history'>('lines')
  const [historyLogs, setHistoryLogs] = useState<DocumentChangeLogDto[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && tab === 'history' && supplierReturn?.id) {
      setHistoryLoading(true)
      setHistoryError(null)
      getSupplierReturnHistory(supplierReturn.id)
        .then(setHistoryLogs)
        .catch((err) => setHistoryError(err instanceof Error ? err.message : 'Lỗi tải lịch sử'))
        .finally(() => setHistoryLoading(false))
    }
  }, [isOpen, tab, supplierReturn?.id])

  if (!isOpen || !supplierReturn) return null

  const isVoid = supplierReturn.status === 'VOID' || supplierReturn.is_void

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div>
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-base">
              <span>Phiếu trả nhà cung cấp: {supplierReturn.code}</span>
              {isVoid ? (
                <Badge tone="danger">Đã hủy</Badge>
              ) : (
                <Badge tone="success">Hoạt động</Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Lý do trả: {supplierReturn.reason} · Người tạo: {supplierReturn.created_by} · Ngày:{' '}
              <span title={supplierReturn.created_at ? formatDateFull(supplierReturn.created_at) : undefined}>
                {supplierReturn.created_at ? formatDate(supplierReturn.created_at) : '—'}
              </span>
            </p>
            {isVoid && supplierReturn.void_reason && (
              <p className="text-xs text-red-600 mt-1 font-medium">
                Lý do hủy: {supplierReturn.void_reason} (bởi {supplierReturn.voided_by || '—'})
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex rounded-[var(--radius-sm)] border border-[var(--color-border)] p-0.5">
            <button
              type="button"
              className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                tab === 'lines'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:bg-slate-50'
              }`}
              onClick={() => setTab('lines')}
            >
              Dòng hàng
            </button>
            <button
              type="button"
              className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                tab === 'history'
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:bg-slate-50'
              }`}
              onClick={() => setTab('history')}
            >
              Lịch sử thay đổi
            </button>
          </div>

          {!isVoid && canWrite && (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={onEdit}>
                <Edit2 className="mr-1 h-3.5 w-3.5" />
                Sửa phiếu
              </Button>
              <Button size="sm" variant="ghost" onClick={onVoid} className="text-red-600 hover:bg-red-50">
                <Ban className="mr-1 h-3.5 w-3.5" />
                Hủy phiếu
              </Button>
            </div>
          )}
        </div>

        <div className="mt-3 max-h-[60vh] overflow-y-auto">
          {tab === 'lines' ? (
            <div className="rounded border border-slate-100 overflow-hidden">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-2">Vật tư</th>
                    <th className="p-2 text-right">SL trả</th>
                    <th className="p-2">ĐVT gốc</th>
                    <th className="p-2">Ghi chú</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(supplierReturn.lines ?? []).map((l) => (
                    <tr key={l.id}>
                      <td className="p-2 font-mono font-medium">{l.material_code || l.material_id}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{l.qty_base}</td>
                      <td className="p-2">{l.base_uom}</td>
                      <td className="p-2 text-slate-500">{l.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <DocumentHistoryTab
              logs={historyLogs}
              loading={historyLoading}
              error={historyError}
              emptyMessage="Chưa có lịch sử thay đổi cho phiếu trả NCC này."
            />
          )}
        </div>

        <div className="mt-4 flex justify-end border-t border-slate-100 pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── RETURNS PAGE ────────────────────────────────────────────────

export function ReturnsPage() {
  const canWriteInventory = useCan('inventory.write')
  const [materials, setMaterials] = useState<Material[]>([])
  const [kind, setKind] = useState<'RETURN_TO_SUPPLIER' | 'RETURN_FROM_CUSTOMER' | 'RETURN_FOR_REWORK'>('RETURN_TO_SUPPLIER')
  const [materialId, setMaterialId] = useState('')
  const [qty, setQty] = useState('50')
  const [reason, setReason] = useState('Hàng lỗi quy cách')
  const [returns, setReturns] = useState<StockMovementDto[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [selectedReturn, setSelectedReturn] = useState<SupplierReturnDto | null>(null)
  const [returnDetailOpen, setReturnDetailOpen] = useState(false)
  const [returnEditOpen, setReturnEditOpen] = useState(false)
  const [returnVoidOpen, setReturnVoidOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  async function loadReturns() {
    try {
      const movs = await listStockMovements()
      const filtered = movs.filter(
        (m) =>
          m.type === 'RETURN_TO_SUPPLIER' ||
          m.type === 'CUSTOMER_RETURN' ||
          m.type === 'REWORK_IN',
      )
      setReturns(filtered)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    async function init() {
      try {
        const list = await materialService.list()
        setMaterials(list)
        if (list.length > 0) setMaterialId(list[0].id)
        await loadReturns()
      } catch {
        // ignore
      }
    }
    void init()
  }, [])

  async function onReturn() {
    setError(null)
    setSuccess(null)
    setLoading(true)
    try {
      const q = Number(qty) || 0
      if (kind === 'RETURN_TO_SUPPLIER') {
        const res = await recordSupplierReturn({
          reason,
          lines: [{ material_id: materialId, qty_base: q }],
        })
        setSuccess(`Đã ghi phiếu trả hàng NCC: ${res.code}`)
      } else if (kind === 'RETURN_FROM_CUSTOMER') {
        const res = await recordCustomerReturn({
          material_id: materialId,
          qty_base: q,
          reason,
        })
        setSuccess(`Đã ghi nhận khách trả hàng: ${res.code}`)
      } else {
        const res = await recordReworkOrder({
          material_id: materialId,
          qty_base: q,
          reason,
        })
        setSuccess(`Đã tạo lệnh tái chế: ${res.code}`)
      }
      await loadReturns()
    } catch (err: any) {
      if (err instanceof InventoryApiClientError && err.code === 'BASE_UOM_MISSING') {
        setError(`Lỗi: ${err.message}. Cần cấu hình đơn vị gốc cho vật tư.`)
      } else {
        const formatted = formatDocumentError(err)
        setError(formatted.message)
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleOpenReturn(id: string) {
    setError(null)
    try {
      const full = await getSupplierReturn(id)
      if (full) {
        setSelectedReturn(full)
        setReturnDetailOpen(true)
      }
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    }
  }

  async function handleSaveReturn(
    lines: Array<{ id: string; qty_base: number; note?: string }>,
    auditReason: string,
    newReason?: string,
    newNote?: string,
  ) {
    if (!selectedReturn) return
    setActionLoading(true)
    setError(null)
    try {
      const updated = await patchSupplierReturn(
        selectedReturn.id,
        { lines, reason: newReason, note: newNote },
        auditReason,
      )
      setSelectedReturn(updated)
      setReturnEditOpen(false)
      setSuccess(`Đã cập nhật phiếu trả NCC ${updated.code} thành công.`)
      await loadReturns()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setActionLoading(false)
    }
  }

  async function handleVoidReturn(voidReason: string) {
    if (!selectedReturn) return
    setActionLoading(true)
    setError(null)
    try {
      const updated = await voidSupplierReturn(selectedReturn.id, voidReason)
      setSelectedReturn(updated)
      setReturnVoidOpen(false)
      setSuccess(`Đã hủy phiếu trả NCC ${updated.code} thành công.`)
      await loadReturns()
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setActionLoading(false)
    }
  }

  const columns: ProtoColumn<StockMovementDto>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'Chứng từ',
        sortable: true,
        width: 120,
        cell: (r) => (
          <span className="font-mono text-xs font-semibold">{r.doc_code}</span>
        ),
        sortValue: (r) => r.doc_code,
      },
      {
        id: 'kind',
        header: 'Loại',
        cell: (r) => r.type,
        sortValue: (r) => r.type,
      },
      {
        id: 'mat',
        header: 'Vật tư',
        cell: (r) => r.material_code || r.material_id,
        sortValue: (r) => r.material_code || r.material_id,
      },
      {
        id: 'qty',
        header: 'SL',
        align: 'right',
        cell: (r) => (
          <span className="tabular-nums">{formatQtyWithUom(r.qty_base, r.base_uom)}</span>
        ),
        sortValue: (r) => r.qty_base,
      },
      {
        id: 'st',
        header: 'Stock Status',
        cell: (r) => <StockStatusBadge status={r.stock_status} />,
        sortValue: (r) => r.stock_status,
      },
      {
        id: 'at',
        header: 'Ngày',
        cell: (r) =>
          r.at ? (
            <span title={formatDateFull(r.at)}>{formatDate(r.at)}</span>
          ) : (
            '—'
          ),
        sortValue: (r) => r.at,
      },
      {
        id: 'by',
        header: 'Người',
        cell: (r) => r.by,
        sortValue: (r) => r.by,
      },
      {
        id: 'note',
        header: 'Ghi chú',
        cell: (r) => r.note || '—',
        sortValue: (r) => r.note || '',
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Kho', to: '/warehouse' },
          { label: 'Trả hàng' },
        ]}
      />
      <PageHeader
        title="Trả hàng / Tái chế"
        subtitle="RETURN TO SUPPLIER ↓ On Hand · FROM CUSTOMER → QUARANTINE · REWORK không Available"
      />

      {error ? <PageError message={error} /> : null}
      {success ? (
        <p className="mb-3 rounded-[var(--radius-md)] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {success}
        </p>
      ) : null}

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Số phiếu" value={returns.length} />
      </div>

      <div className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
        <div className="grid gap-3 xl:grid-cols-2">
          <label className="text-xs text-[var(--color-text-muted)]">
            Loại
            <select
              className="mt-1 block h-8 w-full rounded border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-text)]"
              value={kind}
              onChange={(e) => setKind(e.target.value as any)}
            >
              <option value="RETURN_TO_SUPPLIER">Trả lại NCC (Return to Supplier)</option>
              <option value="RETURN_FROM_CUSTOMER">Khách trả lại (Return from Customer)</option>
              <option value="RETURN_FOR_REWORK">Chuyển tái chế (Rework Order)</option>
            </select>
          </label>
          <label className="text-xs text-[var(--color-text-muted)]">
            Vật tư
            <select
              className="mt-1 block h-8 w-full rounded border border-[var(--color-border)] bg-white px-2 text-sm text-[var(--color-text)]"
              value={materialId}
              onChange={(e) => setMaterialId(e.target.value)}
            >
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} — {m.name} ({m.uom_default || '—'})
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-[var(--color-text-muted)]">
            Số lượng (ĐVT gốc)
            <input
              className="mt-1 block h-8 w-full rounded border border-[var(--color-border)] bg-white px-2 text-sm tabular-nums"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </label>
          <label className="text-xs text-[var(--color-text-muted)]">
            Lý do
            <input
              className="mt-1 block h-8 w-full rounded border border-[var(--color-border)] bg-white px-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        </div>
        <div className="mt-3">
          <Button
            size="sm"
            onClick={() => void onReturn()}
            disabled={loading}
          >
            {loading ? 'Đang ghi nhận...' : 'Ghi trả hàng'}
          </Button>
        </div>
      </div>

      <ProtoExcelTable
        columns={columns}
        rows={returns}
        rowKey={(r) => r.id}
        exportName="tra-hang"
        stickyLeftColumnIds={['code']}
        onRowClick={(r) => {
          if (r.doc_id && r.type === 'RETURN_TO_SUPPLIER') {
            void handleOpenReturn(r.doc_id)
          }
        }}
        emptyMessage="Chưa có phiếu trả hàng."
      />

      <SupplierReturnDetailModal
        isOpen={returnDetailOpen}
        onClose={() => setReturnDetailOpen(false)}
        supplierReturn={selectedReturn}
        canWrite={canWriteInventory}
        onEdit={() => setReturnEditOpen(true)}
        onVoid={() => setReturnVoidOpen(true)}
      />

      <SupplierReturnEditModal
        isOpen={returnEditOpen}
        onClose={() => setReturnEditOpen(false)}
        onSave={handleSaveReturn}
        supplierReturn={selectedReturn}
        loading={actionLoading}
      />

      <VoidDocumentDialog
        isOpen={returnVoidOpen}
        documentCode={selectedReturn?.code ?? ''}
        stockImpactWarning="Hủy phiếu trả NCC sẽ nhập lại toàn bộ số lượng hàng tương ứng về tồn kho khả dụng."
        onClose={() => setReturnVoidOpen(false)}
        onConfirm={handleVoidReturn}
      />
    </div>
  )
}

// ─── TRANSACTIONS PAGE ───────────────────────────────────────────

export function TransactionsPage() {
  const [movements, setMovements] = useState<StockMovementDto[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function init() {
      try {
        const list = await listStockMovements()
        setMovements(list)
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    }
    void init()
  }, [])

  const columns: ProtoColumn<StockMovementDto>[] = useMemo(
    () => [
      {
        id: 'at',
        header: 'Ngày',
        sortable: true,
        cell: (r) =>
          r.at ? (
            <span title={formatDateFull(r.at)}>{formatDate(r.at)}</span>
          ) : (
            '—'
          ),
        sortValue: (r) => r.at,
      },
      {
        id: 'type',
        header: 'Loại',
        cell: (r) => r.type,
        sortValue: (r) => r.type,
      },
      {
        id: 'doc',
        header: 'Chứng từ',
        cell: (r) => r.doc_code,
        sortValue: (r) => r.doc_code,
      },
      {
        id: 'mat',
        header: 'Mã VT',
        cell: (r) => r.material_code || r.material_id,
        sortValue: (r) => r.material_code || r.material_id,
      },
      {
        id: 'qty',
        header: 'Số lượng',
        align: 'right',
        cell: (r) => (
          <span
            className={`tabular-nums ${
              r.qty_base < 0 ? 'text-red-700' : 'text-emerald-700'
            }`}
          >
            {formatQtyWithUom(r.qty_base, r.base_uom)}
          </span>
        ),
        sortValue: (r) => r.qty_base,
      },
      {
        id: 'st',
        header: 'Stock Status',
        cell: (r) => <StockStatusBadge status={r.stock_status} />,
        sortValue: (r) => r.stock_status,
      },
      {
        id: 'by',
        header: 'Người',
        cell: (r) => r.by,
        sortValue: (r) => r.by,
      },
      {
        id: 'note',
        header: 'Ghi chú',
        cell: (r) => r.note || '—',
        sortValue: (r) => r.note || '',
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Kho', to: '/warehouse' },
          { label: 'Lịch sử biến động' },
        ]}
      />
      <PageHeader title="Lịch sử biến động kho" subtitle="Postgres Stock Ledger log" />

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Tổng giao dịch" value={movements.length} />
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-400">Đang tải biến động...</div>
      ) : (
        <ProtoExcelTable
          columns={columns}
          rows={movements}
          rowKey={(r) => r.id}
          exportName="stock-txn"
          stickyLeftColumnIds={['at', 'doc']}
          emptyMessage="Chưa có giao dịch kho."
        />
      )}
    </div>
  )
}
